import React, { useEffect, useState } from 'react';
import Modal from 'components/modal/Modal';
import { useListFiles } from 'api/assets/assetsServices';
import { useFileUpload } from 'hooks/useFileUpload';
import { FileUploadArea } from './FileUploadArea';
import { UploadFileItem } from './UploadFileItem';
import DownloadableFileItem from './DownloadableFileItem';
import Skeleton from 'react-loading-skeleton';
import {
  ALLOWED_FILE_TYPES,
  FILE_UPLOAD_CONFIG,
  formatFileSize,
} from 'lib/fileUploadConstants';
import { type UploadedFile } from 'interfaces/file/File.interface';

interface FileUploadModalProps {
  isOpen: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  id?: string;
  allowedFileTypes?: readonly string[];
  maxFileSize?: number;
  maxFiles?: number;
}

export const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  id,
  handleOpen,
  handleClose,
  allowedFileTypes = ALLOWED_FILE_TYPES.ALL,
  maxFileSize = FILE_UPLOAD_CONFIG.MAX_FILE_SIZE,
  maxFiles = FILE_UPLOAD_CONFIG.MAX_FILES_COUNT,
}) => {
  const [dragActive] = useState(false);
  const { data: filesData, isFetching: isLoadingFiles } = useListFiles(id);

  const {
    uploadItems,
    addFiles,
    retryUpload,
    removeFile,
    clearAll,
    isUploading,
    totalCount,
  } = useFileUpload({
    assetId: id ?? '',
    allowedTypes: allowedFileTypes,
    maxFileSize,
    maxFiles,
  });

  useEffect(() => {
    if (!isOpen) {
      clearAll();
    }
  }, [isOpen, clearAll]);

  const handleFileSelect = (files: File[]): void => {
    addFiles(files);
  };

  const generateAcceptedTypesString = (): string => {
    return allowedFileTypes.join(',');
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Documents">
      <div className="w-full h-full bg-white rounded-lg flex flex-col gap-6">
        {/* Upload Area */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <FileUploadArea
              onFileSelect={handleFileSelect}
              isDragActive={dragActive}
              isUploading={isUploading}
              acceptedFileTypes={generateAcceptedTypesString()}
              maxFileSize={formatFileSize(maxFileSize)}
            />

            {/* Upload Status */}
            {totalCount > 0 && (
              <div className="flex flex-col">
                <div className="flex flex-col w-full max-h-60 overflow-y-auto">
                  {uploadItems.map((uploadItem) => (
                    <UploadFileItem
                      key={uploadItem.key}
                      uploadItem={uploadItem}
                      onRetry={retryUpload}
                      onRemove={removeFile}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Existing Files */}
          <div className="flex flex-col gap-2">
            <h6 className="text-sm font-medium text-gray-700 uppercase">
              {isLoadingFiles
                ? 'All Files'
                : `All Files (${filesData?.files?.length ?? 0})`}
            </h6>
            <div className="flex flex-col gap-2 w-full max-h-60 overflow-y-auto">
              {isLoadingFiles ? (
                Array(3)
                  .fill(0)
                  .map((_, idx) => (
                    <div
                      key={`skeleton-file-${idx}`}
                      className="flex items-center justify-between border-gray-100 rounded-md border px-4 py-3"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Skeleton width={20} height={20} />
                        <div className="flex flex-col gap-1 flex-1">
                          <Skeleton
                            width={`${120 + Math.random() * 80}px`}
                            height={16}
                          />
                          <Skeleton width={60} height={12} />
                        </div>
                      </div>
                      <Skeleton width={24} height={24} />
                    </div>
                  ))
              ) : filesData?.files && filesData.files.length > 0 ? (
                filesData.files.map((file: UploadedFile, idx: number) => (
                  <DownloadableFileItem
                    key={`existing-${idx}`}
                    file={file}
                    assetId={id ?? ''}
                  />
                ))
              ) : (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <p className="text-sm">No files uploaded yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
